import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Tooltip } from '../../components';
import * as partnerService from '../../services/partner-service';
import type { FounderRow } from '../../services/partner-service';

// ──────────────────────────────────────────────────────────────────────────
// Filter option lists
// ──────────────────────────────────────────────────────────────────────────

const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'idea', label: 'Idea' },
  { value: 'mvp', label: 'MVP' },
  { value: 'growth', label: 'Growth' },
  { value: 'scale', label: 'Scale' },
];

const PHASES = [
  { value: '', label: 'All Phases' },
  { value: '1', label: 'Phase 1' },
  { value: '2', label: 'Phase 2' },
  { value: '3', label: 'Phase 3' },
  { value: '4', label: 'Phase 4' },
  { value: '5', label: 'Phase 5' },
  { value: '6', label: 'Phase 6' },
];

const PITCH_STATUSES = [
  { value: '', label: 'All Pitch Statuses' },
  { value: 'none', label: 'Not Started' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
];

// ──────────────────────────────────────────────────────────────────────────
// Styling helpers
// ──────────────────────────────────────────────────────────────────────────

const industryColor: Record<string, string> = {
  Technology: 'bg-chart/10 text-chart border-chart/20',
  HealthTech: 'bg-success/10 text-success border-success/20',
  EdTech: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  Logistics: 'bg-warning/10 text-warning border-warning/20',
  Finance: 'bg-warning/10 text-warning border-warning/20',
  CleanTech: 'bg-chart/10 text-chart border-chart/20',
  AgriTech: 'bg-success/10 text-success border-success/20',
};

const stageLabel: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  growth: 'Growth',
  scale: 'Scale',
};

const pitchBadge = (status: FounderRow['pitchStatus']): string => {
  switch (status) {
    case 'approved':
      return 'bg-success/15 text-success border-success/30';
    case 'published':
      return 'bg-chart/15 text-chart border-chart/30';
    case 'submitted':
      return 'bg-warning/15 text-warning border-warning/30';
    case 'draft':
      return 'bg-surface text-muted border-line';
    default:
      return 'bg-surface text-muted border-line';
  }
};

const pitchLabel: Record<FounderRow['pitchStatus'], string> = {
  none: '—',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  published: 'Published',
};

const attendanceDot = (attended: number, invited: number): string => {
  if (invited === 0) return 'bg-muted';
  const ratio = attended / invited;
  if (ratio >= 0.8) return 'bg-success';
  if (ratio >= 0.5) return 'bg-warning';
  return 'bg-danger';
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const PartnerDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<FounderRow[]>([]);
  const [kpis, setKpis] = useState({ total: 0, inProgram: 0, pitchesApproved: 0, investorReady: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [pitchFilter, setPitchFilter] = useState('');

  useEffect(() => {
    Promise.all([partnerService.getFounderRows(), partnerService.getFounderKpis()])
      .then(([r, k]) => {
        setRows(r);
        setKpis(k);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const industryOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.industry));
    return [
      { value: '', label: 'All Industries' },
      ...Array.from(set).sort().map((i) => ({ value: i, label: i })),
    ];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !q || r.founderName.toLowerCase().includes(q) || r.startupName.toLowerCase().includes(q))
      .filter((r) => !industryFilter || r.industry === industryFilter)
      .filter((r) => !stageFilter || r.stage === stageFilter)
      .filter((r) => !phaseFilter || r.currentPhase === Number(phaseFilter))
      .filter((r) => !pitchFilter || r.pitchStatus === pitchFilter);
  }, [rows, search, industryFilter, stageFilter, phaseFilter, pitchFilter]);

  const anyFilterActive = !!(search || industryFilter || stageFilter || phaseFilter || pitchFilter);

  const clearFilters = () => {
    setSearch('');
    setIndustryFilter('');
    setStageFilter('');
    setPhaseFilter('');
    setPitchFilter('');
  };

  return (
    <div className="space-y-10 pb-10">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Institution Portal</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">Your Founders.</h1>
          <p className="font-serif-display italic text-muted text-lg mt-2">
            Track progress of founders from your institution across the BunyadKar incubation.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:pb-1">
          <Tooltip content="Founders onboarded to BunyadKar">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart tabular-nums">{loading ? '–' : kpis.total}</span>
              <span className="text-muted">Total Founders</span>
            </span>
          </Tooltip>
          <Tooltip content="Founders with an actively in-progress phase">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="w-1.5 h-1.5 rounded-full bg-chart animate-pulse" />
              <span className="font-bold text-chart tabular-nums">{loading ? '–' : kpis.inProgram}</span>
              <span className="text-muted">In Program</span>
            </span>
          </Tooltip>
          <Tooltip content="Founders whose latest pitch has been mentor-approved">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-success tabular-nums">{loading ? '–' : kpis.pitchesApproved}</span>
              <span className="text-muted">Pitches Approved</span>
            </span>
          </Tooltip>
          <Tooltip content="Founders with a published pitch visible to investors">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart tabular-nums">{loading ? '–' : kpis.investorReady}</span>
              <span className="text-muted">Investor-Ready</span>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ─── Search + filters ─── */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M16.5 10.5a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search founder or startup…"
            className="w-full pl-9 pr-3 py-2.5 rounded-card border border-line bg-shell text-text placeholder:text-muted/60 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-44">
            <Select name="industry" options={industryOptions} value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="stage" options={STAGES} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} />
          </div>
          <div className="w-36">
            <Select name="phase" options={PHASES} value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} />
          </div>
          <div className="w-48">
            <Select name="pitch" options={PITCH_STATUSES} value={pitchFilter} onChange={(e) => setPitchFilter(e.target.value)} />
          </div>
          {anyFilterActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-muted hover:text-text underline transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <span className="ml-auto text-xs text-muted tabular-nums">
          {loading ? '—' : `${filtered.length} of ${rows.length} founders`}
        </span>
      </div>

      {/* ─── Table / cards ─── */}
      {loading ? (
        <p className="font-serif-display italic text-muted">Loading your cohort…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-shell border border-line rounded-card p-12 text-center">
          <p className="font-display text-lg tracking-wide text-muted uppercase">No Matches</p>
          <p className="font-serif-display italic text-muted text-sm mt-1 mb-4">Try adjusting your search or filters.</p>
          <button
            onClick={clearFilters}
            className="text-xs text-chart hover:text-chart/80 underline transition-colors"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <>
          {/* Desktop / tablet table */}
          <div className="hidden md:block bg-shell border border-line rounded-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line bg-surface/40">
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Founder</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Startup</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Industry / Stage</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Phase</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Coursework</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted hidden lg:table-cell">Attendance</th>
                    <th className="text-left py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted">Pitch</th>
                    <th className="text-right py-3 px-5 text-[10px] font-semibold uppercase tracking-wider text-muted" aria-label="Open" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map((row, i) => (
                    <tr
                      key={row.startupId}
                      onClick={() => navigate(`/app/partner/startup/${row.startupId}`)}
                      className="group hover:bg-accent-soft/50 cursor-pointer transition-colors animate-fade-in-up"
                      style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'both' }}
                    >
                      {/* Founder */}
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                            {initialsOf(row.founderName)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-text text-sm truncate">{row.founderName}</div>
                            {row.founderRegion && (
                              <div className="text-xs text-muted truncate">{row.founderRegion}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Startup */}
                      <td className="py-4 px-5 max-w-[260px]">
                        <div className="font-semibold text-text text-sm truncate">{row.startupName}</div>
                        <div className="text-xs text-muted truncate">{row.startupTagline}</div>
                      </td>

                      {/* Industry / Stage */}
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                              industryColor[row.industry] || 'bg-surface text-muted border-line'
                            }`}
                          >
                            {row.industry}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                            {stageLabel[row.stage] || row.stage}
                          </span>
                        </div>
                      </td>

                      {/* Phase */}
                      <td className="py-4 px-5 min-w-[200px]">
                        <div className="text-xs font-medium text-text">
                          Phase {row.currentPhase}{' '}
                          <span className="text-muted font-normal">— {row.currentPhaseTitle}</span>
                        </div>
                        <div className="mt-1.5 w-full max-w-[180px] h-1 bg-surface rounded-full overflow-hidden">
                          <div
                            className="h-full bg-chart rounded-full transition-all"
                            style={{ width: `${row.phaseProgressPct}%` }}
                          />
                        </div>
                      </td>

                      {/* Coursework */}
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text tabular-nums">{row.courseworkPct}%</span>
                          <div className="w-14 h-1 bg-surface rounded-full overflow-hidden">
                            <div
                              className="h-full bg-success rounded-full transition-all"
                              style={{ width: `${row.courseworkPct}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Attendance */}
                      <td className="py-4 px-5 hidden lg:table-cell">
                        {row.sessionsInvited > 0 ? (
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${attendanceDot(
                                row.sessionsAttended,
                                row.sessionsInvited
                              )}`}
                            />
                            <span className="text-sm text-text tabular-nums">
                              {row.sessionsAttended}
                              <span className="text-muted">/{row.sessionsInvited}</span>
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted">—</span>
                        )}
                      </td>

                      {/* Pitch */}
                      <td className="py-4 px-5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${pitchBadge(
                            row.pitchStatus
                          )}`}
                        >
                          {pitchLabel[row.pitchStatus]}
                        </span>
                      </td>

                      {/* Chevron */}
                      <td className="py-4 px-5 text-right">
                        <svg
                          className="w-4 h-4 text-muted group-hover:text-chart transition-colors inline-block"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {filtered.map((row, i) => (
              <button
                key={row.startupId}
                onClick={() => navigate(`/app/partner/startup/${row.startupId}`)}
                className="w-full text-left bg-shell border border-line rounded-card p-4 hover:border-chart/40 transition-colors animate-fade-in-up"
                style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs flex-shrink-0">
                    {initialsOf(row.founderName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text text-sm truncate">{row.startupName}</div>
                    <div className="text-xs text-muted truncate">{row.founderName} · {row.founderRegion}</div>
                  </div>
                  <svg className="w-4 h-4 text-muted flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                      industryColor[row.industry] || 'bg-surface text-muted border-line'
                    }`}
                  >
                    {row.industry}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface text-muted border border-line">
                    {stageLabel[row.stage] || row.stage}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${pitchBadge(row.pitchStatus)}`}
                  >
                    {pitchLabel[row.pitchStatus]}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Phase</div>
                    <div className="text-xs font-medium text-text">
                      Phase {row.currentPhase} ({row.phaseProgressPct}%)
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Coursework</div>
                    <div className="text-xs font-medium text-text">{row.courseworkPct}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Sessions</div>
                    <div className="text-xs font-medium text-text tabular-nums">
                      {row.sessionsInvited > 0 ? `${row.sessionsAttended}/${row.sessionsInvited}` : '—'}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}


    </div>
  );
};

export default PartnerDashboard;
